import { AppointmentStatus } from '../appointments/enums/appointment-status.enum.js';
import { TreatmentLocation } from '../calls/enums/treatment-location.enum.js';
import { TreatmentPlanStatus } from '../treatment-plan/enums/treatment-plan-status.enum.js';
import { TreatmentUnitStatus } from '../treatment-plan/enums/treatment-unit-status.enum.js';
import { deriveKombiGroups } from './kombi-mirror.js';
/**
 * Why a plan can't be booked against by the patient (PRD #1343, RS-001/RS-006).
 * A plan that is not `eligible` renders without a booking affordance — the entry
 * screen shows it (or hides it) but never lets the patient tap it.
 */
export var PlanBookingIneligibilityReason;
(function (PlanBookingIneligibilityReason) {
    /** Plan is DRAFT/COMPLETED/CANCELLED/EXPIRED — only ACTIVE plans take bookings. */
    PlanBookingIneligibilityReason["NOT_ACTIVE"] = "NOT_ACTIVE";
    /**
     * A multi-Heilmittel plan with no confirmed appointment history — the Kombi
     * grouping cannot be mirrored, so the patient can't self-book it yet. Surfaced
     * as a "contact the practice" card (RS-006).
     */
    PlanBookingIneligibilityReason["MULTIPLE_HEILMITTEL_NO_HISTORY"] = "MULTIPLE_HEILMITTEL_NO_HISTORY";
    /** Every unit of the bookable group is already booked — nothing left to continue. */
    PlanBookingIneligibilityReason["NO_REMAINING_UNITS"] = "NO_REMAINING_UNITS";
    /** No prior appointment to derive continuity from — no established therapist yet. */
    PlanBookingIneligibilityReason["NO_ESTABLISHED_THERAPIST"] = "NO_ESTABLISHED_THERAPIST";
    /**
     * The plan continues as video, but the patient has no Contact address for the
     * TAN. Only the patient joins a call, so a caregiver's access doesn't help.
     * Surfaced as a "contact the practice" card.
     */
    PlanBookingIneligibilityReason["VIDEO_WITHOUT_CONTACT_ADDRESS"] = "VIDEO_WITHOUT_CONTACT_ADDRESS";
})(PlanBookingIneligibilityReason || (PlanBookingIneligibilityReason = {}));
/**
 * Pure eligibility + continuity resolver for plan-backed patient booking
 * (PRD #1343, RS-001). Given a plan and the appointments its units are linked
 * to, decide whether the patient may book a follow-up and, if so, resolve the
 * single Heilmittel, the remaining allowance, the next unbooked unit, and the
 * established therapist/duration/location.
 *
 * No I/O, no clock — the caller fetches the plan + linked appointments and
 * feeds them in, so the whole decision is trivially unit-testable.
 *
 * @param plan the patient's treatment plan (with units + their treatment concept)
 * @param linkedAppointments the appointments the plan's units point at (any subset)
 * @param videoReachable whether the patient has a Contact address for a video TAN
 */
export function resolvePlanBookingContext(plan, linkedAppointments, videoReachable = true) {
    if (plan.status !== TreatmentPlanStatus.ACTIVE) {
        return { eligible: false, reason: PlanBookingIneligibilityReason.NOT_ACTIVE };
    }
    const distinctConceptIds = new Set(plan.treatmentUnits.map((u) => u.treatment?.id).filter((id) => id != null));
    const isMultiHeilmittel = distinctConceptIds.size > 1;
    // Established therapist: the most recent non-cancelled linked appointment.
    const priorForPlan = linkedAppointments
        .filter((a) => a.status !== AppointmentStatus.CANCELLED && a.therapistId != null)
        .sort((a, b) => startMs(b) - startMs(a));
    const continuity = priorForPlan[0];
    if (!continuity || continuity.therapistId == null) {
        // No confirmed history → no continuity, and for a Kombi plan no grouping to
        // mirror. Multi-Heilmittel plans surface a "contact the practice" card;
        // single-Heilmittel plans are simply not bookable yet.
        return {
            eligible: false,
            reason: isMultiHeilmittel
                ? PlanBookingIneligibilityReason.MULTIPLE_HEILMITTEL_NO_HISTORY
                : PlanBookingIneligibilityReason.NO_ESTABLISHED_THERAPIST,
        };
    }
    // Kombi mirror: the group of Heilmittel booked together in the plan's last
    // confirmed session (RS-006). `groups[0]` is the most-recent-session group —
    // the shape the patient continues. For a single-Heilmittel plan it is the one
    // Heilmittel; for a Kombi plan it may be a subset of the plan's Heilmittel.
    const groups = deriveKombiGroups(plan, linkedAppointments);
    const bookableGroupConceptIds = groups[0] ?? [];
    // The next unbooked unit of each grouped Heilmittel, ordered by that unit's
    // sequence so the primary (search) Heilmittel is the group's earliest.
    const groupMembers = bookableGroupConceptIds
        .map((conceptId) => nextUnbookedForConcept(plan, conceptId))
        .filter((m) => m != null)
        .sort((a, b) => a.nextUnit.sequenceInPlan - b.nextUnit.sequenceInPlan);
    // A group member with zero unbooked units means the group's last session
    // can't be replayed (a Heilmittel is exhausted) — nothing to continue.
    if (groupMembers.length !== bookableGroupConceptIds.length || groupMembers.length === 0) {
        return { eligible: false, reason: PlanBookingIneligibilityReason.NO_REMAINING_UNITS };
    }
    const primary = groupMembers[0].concept;
    // Complete sessions bookable = the scarcest grouped Heilmittel's remaining units.
    const remainingUnits = Math.min(...groupMembers.map((m) => m.remaining));
    const { minWeeklyFrequency, maxWeeklyFrequency } = deriveWeeklyFrequency(plan);
    const defaultWeeklyCount = Math.min(remainingUnits, maxWeeklyFrequency);
    const treatmentLocation = continuity.treatmentLocation ?? TreatmentLocation.IN_PRESENCE;
    if (treatmentLocation === TreatmentLocation.VIDEO_CALL && !videoReachable) {
        return {
            eligible: false,
            reason: PlanBookingIneligibilityReason.VIDEO_WITHOUT_CONTACT_ADDRESS,
        };
    }
    return {
        eligible: true,
        context: {
            treatmentPlanId: plan.id,
            officeLocationId: plan.officeLocationId,
            treatmentConceptId: primary.id,
            treatmentCode: primary.code,
            treatmentName: primary.name,
            groupTreatmentConceptIds: groupMembers.map((m) => m.concept.id),
            treatmentLabel: groupMembers.map((m) => m.concept.name).join(' + '),
            remainingUnits,
            nextBillableServiceItemId: groupMembers[0].nextUnit.billableServiceItemId,
            nextBillableServiceItemIds: groupMembers.map((m) => m.nextUnit.billableServiceItemId),
            minWeeklyFrequency,
            maxWeeklyFrequency,
            defaultWeeklyCount,
            therapistId: continuity.therapistId,
            therapistName: therapistDisplayName(continuity),
            therapistDurationMin: continuity.plannedDuration,
            treatmentLocation,
        },
    };
}
/**
 * The next unbooked unit (lowest sequence) of a Heilmittel plus its remaining
 * unbooked count, or `null` when the Heilmittel has no unbooked units left.
 */
function nextUnbookedForConcept(plan, conceptId) {
    const unbooked = plan.treatmentUnits
        .filter((u) => u.treatment?.id === conceptId &&
        u.status === TreatmentUnitStatus.PLANNED &&
        u.appointmentId === null)
        .sort((a, b) => a.sequenceInPlan - b.sequenceInPlan);
    const nextUnit = unbooked[0];
    if (!nextUnit || !nextUnit.treatment)
        return null;
    const { id, code, name } = nextUnit.treatment;
    return { concept: { id, code, name }, nextUnit, remaining: unbooked.length };
}
/** The slot-finder's weekly-slot band is capped at [1,3]; clamp there. */
const MIN_WEEKLY_SLOTS = 1;
const MAX_WEEKLY_SLOTS = 3;
function clampWeekly(value) {
    return Math.max(MIN_WEEKLY_SLOTS, Math.min(MAX_WEEKLY_SLOTS, Math.round(value)));
}
/**
 * The prescribed weekly rhythm, reduced to the [1,3] band the earliest-slots
 * search accepts (RS-004). A plan may carry only a min, only a max, or neither
 * (older/private plans); we default a missing bound to the other, and both
 * missing to once-weekly, so a suggested batch is always producible.
 */
function deriveWeeklyFrequency(plan) {
    const rawMin = plan.prescriptionData?.minWeeklyTherapyFrequency ?? null;
    const rawMax = plan.prescriptionData?.maxWeeklyTherapyFrequency ?? null;
    const min = clampWeekly(rawMin ?? rawMax ?? MIN_WEEKLY_SLOTS);
    const max = clampWeekly(rawMax ?? rawMin ?? min);
    // Never let a garbled prescription invert the band.
    return { minWeeklyFrequency: Math.min(min, max), maxWeeklyFrequency: Math.max(min, max) };
}
function startMs(a) {
    return a.scheduledStart instanceof Date
        ? a.scheduledStart.getTime()
        : new Date(a.scheduledStart).getTime();
}
function therapistDisplayName(a) {
    const first = a.therapist?.firstName ?? '';
    const last = a.therapist?.lastName ?? '';
    return `${first} ${last}`.trim();
}
//# sourceMappingURL=plan-booking-context.js.map